import { useState } from 'react';
import { Scale, CheckCircle2, AlertCircle } from 'lucide-react';
import { useTrialBalance } from '@/hooks/useAccounting';
import { ACCOUNT_TYPE_LABELS, type AccountType } from '@/types/accounting';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { AccountTypeBadge } from '@/components/accounting/AccountTypeBadge';
import { formatCurrency } from '@/lib/utils';

const TYPE_ORDER: AccountType[] = ['asset', 'liability', 'equity', 'revenue', 'expense'];

export function TrialBalancePage() {
  const [asOf, setAsOf] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const { data, isLoading, error } = useTrialBalance(asOf);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load trial balance"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  if (!data || data.rows.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={Scale}
          title="No activity to report"
          description="Post your first journal entries, then return here for the trial balance."
        />
      </Card>
    );
  }

  const grouped = TYPE_ORDER.map((t) => ({
    type: t,
    rows: data.rows.filter((r) => r.accountType === t),
  })).filter((g) => g.rows.length > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="kicker mb-1">Trial Balance</div>
          <div className="text-sm text-text-muted">
            Sums of debit and credit balances across every account with activity. Must balance.
          </div>
        </div>
        <div className="w-56">
          <Input
            label="As of"
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </div>
      </div>

      {/* Balance check */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <div className="kicker mb-1">Balance Check</div>
            <div className="text-sm text-text-muted">
              Total debits must equal total credits.
            </div>
          </div>
          {data.balanced ? (
            <Badge tone="success"><CheckCircle2 size={10} /> Books Balance</Badge>
          ) : (
            <Badge tone="danger"><AlertCircle size={10} /> Out of Balance</Badge>
          )}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-px bg-border rounded-sm overflow-hidden border border-border">
          <Stat label="Total Debits"  value={formatCurrency(data.totalDebit)} />
          <Stat label="Total Credits" value={formatCurrency(data.totalCredit)} />
          <Stat label="Difference"
            value={formatCurrency(Math.abs(data.totalDebit - data.totalCredit))}
            alert={!data.balanced}
          />
        </div>
      </Card>

      {/* Accounts */}
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left bg-bg-subtle">
              <th className="kicker px-6 py-3 font-normal w-24">Account</th>
              <th className="kicker px-6 py-3 font-normal">Name</th>
              <th className="kicker px-6 py-3 font-normal">Type</th>
              <th className="kicker px-6 py-3 font-normal text-right">Debit</th>
              <th className="kicker px-6 py-3 font-normal text-right">Credit</th>
            </tr>
          </thead>
          <tbody>
            {grouped.map(({ type, rows }) => {
              const subDebit = rows.reduce((s, r) => s + r.totalDebit, 0);
              const subCredit = rows.reduce((s, r) => s + r.totalCredit, 0);
              return (
                <>
                  <tr key={`${type}-hdr`} className="border-b border-border bg-bg-subtle/50">
                    <td colSpan={5} className="px-6 py-2 kicker">{ACCOUNT_TYPE_LABELS[type]}</td>
                  </tr>
                  {rows.map((r) => (
                    <tr key={r.accountId} className="border-b border-border hover:bg-bg-hover/40">
                      <td className="px-6 py-3 font-mono text-brass-300">{r.accountNumber}</td>
                      <td className="px-6 py-3 text-text">{r.accountName}</td>
                      <td className="px-6 py-3"><AccountTypeBadge type={r.accountType} /></td>
                      <td className="px-6 py-3 text-right tabular text-text">
                        {r.totalDebit > 0 ? formatCurrency(r.totalDebit) : <span className="text-text-dim">—</span>}
                      </td>
                      <td className="px-6 py-3 text-right tabular text-text">
                        {r.totalCredit > 0 ? formatCurrency(r.totalCredit) : <span className="text-text-dim">—</span>}
                      </td>
                    </tr>
                  ))}
                  <tr key={`${type}-sub`} className="border-b border-border-strong">
                    <td colSpan={3} className="px-6 py-2 text-right">
                      <span className="text-2xs text-text-dim uppercase tracking-ultra">Subtotal</span>
                    </td>
                    <td className="px-6 py-2 text-right tabular text-text-muted">{formatCurrency(subDebit)}</td>
                    <td className="px-6 py-2 text-right tabular text-text-muted">{formatCurrency(subCredit)}</td>
                  </tr>
                </>
              );
            })}
          </tbody>
          <tfoot className="bg-bg-subtle">
            <tr className="border-t-2 border-brass-400">
              <td colSpan={3} className="px-6 py-4 text-right">
                <span className="kicker">Totals</span>
              </td>
              <td className="px-6 py-4 text-right tabular font-display text-lg text-brass-300">
                {formatCurrency(data.totalDebit)}
              </td>
              <td className="px-6 py-4 text-right tabular font-display text-lg text-brass-300">
                {formatCurrency(data.totalCredit)}
              </td>
            </tr>
          </tfoot>
        </table>
      </Card>
    </div>
  );
}

function Stat({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-xl tabular ${alert ? 'text-danger' : 'text-text'}`}>{value}</div>
    </div>
  );
}
